import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_IDENTIFICATION_URL } from '@utils/api/endpoints.voice-profiles.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { VOICE_PROFILE_QUERY_KEYS } from '@utils/api/organizations/speakers/voice-profiles/query-keys.ts';

interface AssignPayload {
  speakerLabel: string;
  /** A directory speaker id, or null for an explicit "unknown" decision. */
  speakerId: number | null;
}

/** Record a human identity decision for one original diarization label.
 *
 * Works before any recognition job or voice profile exists, so assigning a
 * person is never gated on enrollment — and, crucially, assigning a person
 * never creates a voice sample as a side effect. The two are separate
 * explicit actions.
 *
 * `speakerId: null` is an explicit unknown, which is different from having no
 * decision at all: it survives reruns the same way a named choice does. */
export const useAssignVoiceIdentity = (
  sessionId: string | undefined,
  documentId: string | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError<BaseError>, AssignPayload>(
    async ({ speakerLabel, speakerId }) => {
      await Axios.put(
        VOICE_IDENTIFICATION_URL.override(
          sessionId as string,
          documentId as string,
          speakerLabel
        ),
        { speaker_id: speakerId }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.identifications(
            sessionId ?? '',
            documentId ?? ''
          )
        );
      },
      onError: () => {
        /* The caller renders a specific message next to the speaker. */
      },
      useErrorBoundary: false
    }
  );
};

/** Clear the human decision so the model's suggestion applies again.
 *  Distinct from choosing "unknown": this removes the override entirely. */
export const useClearVoiceIdentityOverride = (
  sessionId: string | undefined,
  documentId: string | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError<BaseError>, { speakerLabel: string }>(
    async ({ speakerLabel }) => {
      await Axios.delete(
        VOICE_IDENTIFICATION_URL.clearOverride(
          sessionId as string,
          documentId as string,
          speakerLabel
        )
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.identifications(
            sessionId ?? '',
            documentId ?? ''
          )
        );
      },
      onError: () => {
        /* The caller renders a specific message next to the speaker. */
      },
      useErrorBoundary: false
    }
  );
};

/** Request or rerun recognition for a finished, retained session.
 *
 * Repeated requests reuse the existing job rather than queueing another, and a
 * rerun keeps human overrides for labels still present in the transcript — so
 * offering this after enrolling a new person is safe. Skipped sessions are
 * never retried automatically; this is the way to run them again. */
export const useRequestVoiceIdentification = (
  sessionId: string | undefined,
  documentId: string | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation<{ status: string }, AxiosError<BaseError>, void>(
    async () => {
      const { data } = await Axios.post<{ status: string }>(
        VOICE_IDENTIFICATION_URL.identifications(
          sessionId as string,
          documentId as string
        )
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.identifications(
            sessionId ?? '',
            documentId ?? ''
          )
        );
      },
      onError: () => {
        /* Shown in the recognition status panel. */
      },
      useErrorBoundary: false
    }
  );
};
